import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, PlusIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import ExportMenu from '../../components/ui/ExportMenu';
const DisputeList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  // Mock Data
  const disputes = [{
    id: 'DSP-2023-001',
    txId: 'TXN-99281',
    merchant: 'TechStore PK',
    type: 'Fraud',
    amount: 45000,
    date: '2023-10-24',
    status: 'Under Review',
    sla: '2 days'
  }, {
    id: 'DSP-2023-002',
    txId: 'TXN-99282',
    merchant: 'Fashion Hub',
    type: 'Refund',
    amount: 12500,
    date: '2023-10-23',
    status: 'Evidence Submitted',
    sla: '5 days'
  }, {
    id: 'DSP-2023-003',
    txId: 'TXN-99283',
    merchant: 'Grocery Plus',
    type: 'Retrieval',
    amount: 3400,
    date: '2023-10-22',
    status: 'Merchant Notified',
    sla: '1 day'
  }, {
    id: 'DSP-2023-004',
    txId: 'TXN-99284',
    merchant: 'Electro World',
    type: 'Fraud',
    amount: 89000,
    date: '2023-10-21',
    status: 'Created',
    sla: '7 days'
  }, {
    id: 'DSP-2023-005',
    txId: 'TXN-99285',
    merchant: 'Travel Agency',
    type: 'Refund',
    amount: 150000,
    date: '2023-10-20',
    status: 'Approved',
    sla: '-'
  }, {
    id: 'DSP-2023-006',
    txId: 'TXN-99286',
    merchant: 'Book Haven',
    type: 'Retrieval',
    amount: 1200,
    date: '2023-10-19',
    status: 'Rejected',
    sla: '-'
  }, {
    id: 'DSP-2023-007',
    txId: 'TXN-99287',
    merchant: 'TechStore PK',
    type: 'Fraud',
    amount: 32000,
    date: '2023-10-18',
    status: 'Chargeback Initiated',
    sla: '-'
  }, {
    id: 'DSP-2023-008',
    txId: 'TXN-99288',
    merchant: 'Fitness Pro',
    type: 'Refund',
    amount: 8500,
    date: '2023-10-17',
    status: 'Closed',
    sla: '-'
  }];
  const filteredDisputes = disputes.filter(d => {
    const matchesSearch = d.id.toLowerCase().includes(searchTerm.toLowerCase()) || d.merchant.toLowerCase().includes(searchTerm.toLowerCase()) || d.txId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const columns = [{
    header: 'Dispute ID',
    accessor: 'id',
    sortable: true,
    className: 'font-medium text-blue-600 dark:text-blue-400'
  }, {
    header: 'Transaction ID',
    accessor: 'txId',
    sortable: true
  }, {
    header: 'Merchant',
    accessor: 'merchant',
    sortable: true
  }, {
    header: 'Type',
    accessor: 'type',
    sortable: true
  }, {
    header: 'Amount',
    accessor: (row: any) => `PKR ${row.amount.toLocaleString()}`,
    sortable: true
  }, {
    header: 'Date',
    accessor: 'date',
    sortable: true
  }, {
    header: 'Status',
    accessor: (row: any) => {
      let statusType: any = 'default';
      if (row.status === 'Approved' || row.status === 'Closed') statusType = 'success';
      if (row.status === 'Under Review' || row.status === 'Chargeback Initiated') statusType = 'warning';
      if (row.status === 'Rejected') statusType = 'danger';
      if (row.status === 'Evidence Submitted' || row.status === 'Merchant Notified') statusType = 'info';
      return <StatusBadge status={statusType} text={row.status} data-id="element-4050" />;
    },
    sortable: true
  }, {
    header: 'SLA',
    accessor: 'sla'
  }];
  const handleExport = (format: string) => {
    console.log(`Exporting disputes as ${format}`);
    // Implementation for export
  };
  return <div className="space-y-6" data-id="element-4051">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4052">
        <div data-id="element-4053">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4054">
            All Disputes
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4055">
            Manage and track all transaction disputes across the system.
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-4056">
          <ExportMenu onExport={handleExport} data-id="element-4057" />
          <button onClick={() => navigate('/disputes/bulk-upload')} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4058">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-4059" />
            Bulk Upload
          </button>
        </div>
      </div>

      <Card data-id="element-4060">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6" data-id="element-4061">
          <div className="relative flex-1 max-w-md" data-id="element-4062">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4063">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-4064" />
            </div>
            <input type="text" placeholder="Search by Dispute ID, TXN ID, or Merchant..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-4065" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-4066">
            <div className="flex items-center space-x-2" data-id="element-4067">
              <FilterIcon className="h-5 w-5 text-gray-400" data-id="element-4068" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-4069">
                <option value="All" data-id="element-4070">All Statuses</option>
                <option value="Created" data-id="element-4071">Created</option>
                <option value="Merchant Notified" data-id="element-4072">Merchant Notified</option>
                <option value="Evidence Submitted" data-id="element-4073">Evidence Submitted</option>
                <option value="Under Review" data-id="element-4074">Under Review</option>
                <option value="Approved" data-id="element-4075">Approved</option>
                <option value="Rejected" data-id="element-4076">Rejected</option>
                <option value="Chargeback Initiated" data-id="element-4077">
                  Chargeback Initiated
                </option>
                <option value="Closed" data-id="element-4078">Closed</option>
              </select>
            </div>
          </div>
        </div>

        <Table columns={columns} data={filteredDisputes} keyField="id" onRowClick={row => navigate(`/disputes/${row.id}`)} data-id="element-4079" />
      </Card>
    </div>;
};
export default DisputeList;